import { CopyableValue } from '@/components/CopyableValue/CopyableValue'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import type { LicenseResponseBody } from '@/features/Licenses/api/licenseHooks'
import type { LicenseActionId } from '@/features/Licenses/lib/deriveActions'
import {
  Box,
  Button,
  Clipboard,
  DataList,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Check, Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const AUDIT_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  timeZone: 'UTC',
})

/**
 * Validity is a date range, not a moment — no clock time on these two, and so
 * no UTC suffix either. The suffix earns its place on the audit timestamps,
 * where a time of day without a zone is genuinely ambiguous; on a bare date it
 * is noise.
 */
const VALIDITY_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

const RELATIVE_TIME = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' })

const MS_PER_DAY = 1000 * 60 * 60 * 24

/**
 * Returns a human-friendly annotation for the validity end relative to now.
 *
 *   ACTIVE,  expires in 28 days  → "expires in 28 days"
 *   ACTIVE,  expires in 1 day    → "expires tomorrow"
 *   EXPIRED, expired 12 days ago → "expired 12 days ago"
 *   REVOKED, n/a                 → no annotation (status badge already covers it)
 */
function relativeValidity(
  license: LicenseResponseBody,
  now: number,
): string | null {
  if (license.status === 'REVOKED') {
    return null
  }
  const diffMs = license.validityEnd - now
  const diffDays = Math.round(diffMs / MS_PER_DAY)
  // Tense comes from the raw millisecond delta, not the rounded day count: a
  // licence that lapsed an hour ago rounds to 0 days, and testing `diffDays >= 0`
  // announced "expires today" for something already dead. The verb carries the
  // tense, so a 0 still reads correctly on both sides — "expires today" ahead
  // of the deadline, "expired today" just past it.
  if (diffMs >= 0) {
    return `expires ${RELATIVE_TIME.format(diffDays, 'day')}`
  }
  return `expired ${RELATIVE_TIME.format(diffDays, 'day')}`
}

type Props = {
  license: LicenseResponseBody
  onAction: (id: LicenseActionId) => void
  /** Injectable for tests; the relative annotation is read against this. */
  now?: number
}

/**
 * The license's identity card: description, key, package, subscription /
 * reference, validity, and audit timestamps at the bottom. Name, status, and
 * origin are deliberately *not* repeated here — all three sit beside the page
 * title, and showing them twice on one screen was pure duplication. Edit button
 * opens the metadata edit form (name, description, and — for licenses without a
 * Stripe subscription — reference, mutable since APP-2262; origin, subscription
 * and key remain immutable). The values shown here stay read-only regardless:
 * this is the card you read, the dialog is where you change things.
 */
export function MetadataPanel({
  license,
  onAction,
  now = Date.now(),
}: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const dash = String(t('licenses.detail.metadata.emptyDash'))
  const utcSuffix = String(t('licenses.detail.metadata.auditUtcSuffix'))
  const copyKeyLabel = String(t('licenses.detail.metadata.copyKeyButton'))

  const formatAudit = (epochMs: number | undefined) => {
    if (epochMs == null) {
      return dash
    }
    return `${AUDIT_DATE_FORMAT.format(new Date(epochMs))} ${utcSuffix}`
  }

  const relative = relativeValidity(license, now)

  return (
    <SectionCard
      h="100%"
      title={String(t('licenses.detail.metadata.title'))}
      actions={
        <>
          {/* The key itself is no longer displayed — it is a long composite
              string nobody reads, only copies. `Clipboard.Indicator` owns the
              copied state, so there is no timer to leak on unmount.
              `aria-label` is set explicitly because `Clipboard.Trigger`
              otherwise injects its own ("Copy to clipboard"), which would
              override the visible label for screen-reader users. */}
          <Clipboard.Root value={license.licenseKey}>
            <Clipboard.Trigger asChild>
              <Button variant="outline" size="sm" aria-label={copyKeyLabel}>
                <Clipboard.Indicator copied={<Icon as={Check} />}>
                  <Icon as={Copy} />
                </Clipboard.Indicator>
                {copyKeyLabel}
              </Button>
            </Clipboard.Trigger>
          </Clipboard.Root>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAction('editMetadata')}
          >
            {String(t('licenses.detail.metadata.editButton'))}
          </Button>
        </>
      }
    >
      <VStack align="stretch" gap={4}>
        {license.description ? (
          <Text fontSize="sm" color="text.text-subtle">
            {license.description}
          </Text>
        ) : null}

        {/* `DataList` + `CopyableValue`, the same label/value pairing the org
            and hive cards use — every identifier on the page then reads and
            copies the same way. Vertical orientation because this card is one
            of three columns, too narrow for a label beside a UUID.
            Absent values fall back to a plain dash rather than the shared
            "N/A": an SLG licence legitimately has no subscription id, and
            flagging that would read as a fault. */}
        <DataList.Root orientation="vertical" gap={4}>
          <DataList.Item>
            <DataList.ItemLabel>
              {String(t('licenses.detail.metadata.package'))}
            </DataList.ItemLabel>
            <DataList.ItemValue>
              <CopyableValue
                value={license.package}
                hasCopy={false}
                emptyText={dash}
              />
            </DataList.ItemValue>
          </DataList.Item>

          <DataList.Item>
            <DataList.ItemLabel>
              {String(t('licenses.detail.metadata.id'))}
            </DataList.ItemLabel>
            <DataList.ItemValue>
              <CopyableValue value={license.id} fontFamily="mono" />
            </DataList.ItemValue>
          </DataList.Item>

          <DataList.Item>
            <DataList.ItemLabel>
              {String(t('licenses.detail.metadata.subscriptionId'))}
            </DataList.ItemLabel>
            <DataList.ItemValue>
              <CopyableValue
                value={license.subscriptionId}
                fontFamily="mono"
                emptyText={dash}
              />
            </DataList.ItemValue>
          </DataList.Item>

          <DataList.Item>
            <DataList.ItemLabel>
              {String(t('licenses.detail.metadata.reference'))}
            </DataList.ItemLabel>
            <DataList.ItemValue>
              <CopyableValue
                value={license.reference}
                fontFamily="mono"
                emptyText={dash}
              />
            </DataList.ItemValue>
          </DataList.Item>
        </DataList.Root>

        {/* Validity, moved off the page header. As a lone line of dates under
            the title it read as a caption; here it sits with the rest of the
            licence's facts. Split into two labelled fields rather than an
            "x → y" range so each date is its own answerable question, matching
            the two columns on the org licence table. The relative annotation
            hangs off "Valid until" — it is that date, said in the way an
            operator actually asks it ("how long have I got?"). */}
        <DataList.Root
          orientation="vertical"
          flexDirection="row"
          gap={6}
          css={{ '& .chakra-data-list__item': { flex: 1, minWidth: 0 } }}
        >
          <DataList.Item>
            <DataList.ItemLabel>
              {String(t('licenses.detail.metadata.validFrom'))}
            </DataList.ItemLabel>
            <DataList.ItemValue>
              <CopyableValue
                value={VALIDITY_DATE_FORMAT.format(
                  new Date(license.validityStart),
                )}
                hasCopy={false}
              />
            </DataList.ItemValue>
          </DataList.Item>
          <DataList.Item>
            <DataList.ItemLabel>
              {String(t('licenses.detail.metadata.validUntil'))}
            </DataList.ItemLabel>
            <DataList.ItemValue flexDirection="column" alignItems="start">
              <CopyableValue
                value={VALIDITY_DATE_FORMAT.format(
                  new Date(license.validityEnd),
                )}
                hasCopy={false}
              />
              {relative ? (
                <Text fontSize="xs" color="text.text-subtle">
                  {relative}
                </Text>
              ) : null}
            </DataList.ItemValue>
          </DataList.Item>
        </DataList.Root>

        {/* Audit timestamps at the bottom — different category of info, and
            nothing anyone copies, so no copy affordance on these two. The two
            share a row rather than stacking: they are short, they pair
            naturally, and stacked they pushed the card taller than the two
            beside it for no gain. `flex="1"` keeps them evenly split, `minW={0}`
            lets the timestamps wrap instead of forcing the column wider. */}
        <Box pt={3} borderTopWidth="1px" borderColor="border.muted">
          <DataList.Root
            orientation="vertical"
            flexDirection="row"
            gap={6}
            css={{ '& .chakra-data-list__item': { flex: 1, minWidth: 0 } }}
          >
            <DataList.Item>
              <DataList.ItemLabel>
                {String(t('licenses.detail.metadata.createdAt'))}
              </DataList.ItemLabel>
              <DataList.ItemValue>
                <CopyableValue
                  value={formatAudit(license.createdAt)}
                  hasCopy={false}
                />
              </DataList.ItemValue>
            </DataList.Item>
            <DataList.Item>
              <DataList.ItemLabel>
                {String(t('licenses.detail.metadata.updatedAt'))}
              </DataList.ItemLabel>
              <DataList.ItemValue>
                <CopyableValue
                  value={formatAudit(license.updatedAt)}
                  hasCopy={false}
                />
              </DataList.ItemValue>
            </DataList.Item>
          </DataList.Root>
        </Box>
      </VStack>
    </SectionCard>
  )
}
